"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import {
  Field,
  TextInput,
  RememberedTextInput,
  RememberedNamesNote,
  TextArea,
  SelectInput,
  ResultBanner,
  GuardedPrintButton,
  CompletionPanel,
  PrintFooter,
  trackToolResult,
  EvidenceFields,
  EvidencePrint,
  evidenceRequiredItems,
  useSavedEvidence,
  type Evidence,
  type Verdict,
} from "@/components/tools/ui";

/* RIDDOR 2013 (SI 2013/1471). The reportability aid below follows reg 4
   (non-fatal injuries to workers), reg 6 (non-workers taken to hospital),
   reg 7 + Schedule 2 (dangerous occurrences) and the Schedule 1 reporting
   procedure. Deadlines: death/specified injury/dangerous occurrence — notify
   without delay and report online within 10 days; over-7-day incapacitation —
   report within 15 days. Source: legislation.gov.uk/uksi/2013/1471 and
   HSE INDG453. The aid is guidance only — borderline cases go to HSE. */

type IncidentType =
  | "near-miss"
  | "minor-injury"
  | "injury"
  | "dangerous-occurrence"
  | "property-damage";

const TYPE_OPTIONS: { value: IncidentType; label: string }[] = [
  { value: "near-miss", label: "Near miss (no injury, no damage)" },
  { value: "minor-injury", label: "Minor injury (first aid only)" },
  { value: "injury", label: "Injury (more than first aid)" },
  { value: "dangerous-occurrence", label: "Dangerous occurrence" },
  { value: "property-damage", label: "Property / plant damage" },
];

const TYPE_LABEL: Record<IncidentType, string> = Object.fromEntries(
  TYPE_OPTIONS.map((o) => [o.value, o.label]),
) as Record<IncidentType, string>;

/* The guided RIDDOR questions only make sense for injury / dangerous-occurrence
   reports. We keep them simple yes / no / unsure. */
type YN = "" | "yes" | "no" | "unsure";

interface RiddorAnswers {
  death: YN;
  specified: YN;
  overSeven: YN;
  nonWorkerHospital: YN;
  dangerousOccurrence: YN;
}

const EMPTY_RIDDOR: RiddorAnswers = {
  death: "",
  specified: "",
  overSeven: "",
  nonWorkerHospital: "",
  dangerousOccurrence: "",
};

interface PersonRow {
  id: number;
  name: string;
  role: string;
  company: string;
}

interface ActionRow {
  id: number;
  action: string;
  owner: string;
  due: string;
}

let nextPerson = 1;
let nextAction = 1;
const emptyPerson = (): PersonRow => ({ id: nextPerson++, name: "", role: "", company: "" });
const emptyAction = (): ActionRow => ({ id: nextAction++, action: "", owner: "", due: "" });

const SPECIFIED_INJURIES = [
  "A bone fracture — other than to a finger, thumb or toe",
  "Amputation of an arm, hand, finger, thumb, leg, foot or toe",
  "Permanent loss of sight or reduction of sight in one or both eyes",
  "A crush injury to the head or torso causing damage to the brain or internal organs",
  "A serious burn (including scalding) covering more than 10% of the body, or damaging the eyes, respiratory system or other vital organs",
  "Any scalping requiring hospital treatment",
  "Loss of consciousness caused by a head injury or asphyxia",
  "Injury from working in an enclosed space leading to hypothermia / heat illness, or needing resuscitation or 24+ hours in hospital",
];

const DANGEROUS_OCCURRENCE_EXAMPLES = [
  "Collapse, overturning or failure of lifting equipment (crane, hoist, MEWP)",
  "Collapse or partial collapse of a scaffold over 5 m high",
  "Electrical short circuit or overload causing fire or explosion",
  "Unintended collapse of a building or structure under construction",
  "Accidental release of a substance that could cause injury to health",
  "Plant or equipment coming into contact with overhead power lines",
  "Failure of pressure systems, or explosion / fire stopping work for 24+ hours",
];

type RiddorVerdict = {
  outcome: "likely" | "record" | "unsure";
  headline: string;
  deadline: string;
  detail: string;
};

function assessRiddor(type: IncidentType, a: RiddorAnswers): RiddorVerdict {
  const reportableYes =
    a.death === "yes" ||
    a.specified === "yes" ||
    a.overSeven === "yes" ||
    a.nonWorkerHospital === "yes" ||
    a.dangerousOccurrence === "yes";

  if (reportableYes) {
    // Over-7-day incapacitation alone gets the 15-day deadline; the more
    // serious triggers (death, specified injury, non-worker hospital,
    // dangerous occurrence) require notification without delay + 10 days.
    const onlyOverSeven =
      a.overSeven === "yes" &&
      a.death !== "yes" &&
      a.specified !== "yes" &&
      a.nonWorkerHospital !== "yes" &&
      a.dangerousOccurrence !== "yes";

    if (onlyOverSeven) {
      return {
        outcome: "likely",
        headline: "Likely reportable under RIDDOR — over-7-day incapacitation",
        deadline: "Report online within 15 days of the accident.",
        detail:
          "A worker unable to do their normal duties for more than seven consecutive days (not counting the day of the accident) is reportable. Report at hse.gov.uk/riddor.",
      };
    }

    return {
      outcome: "likely",
      headline: "Likely reportable under RIDDOR — report without delay",
      deadline:
        "Notify HSE without delay, then submit the online report within 10 days of the incident.",
      detail:
        "Deaths, specified injuries, non-workers taken to hospital for treatment, and listed dangerous occurrences must be reported. Report at hse.gov.uk/riddor.",
    };
  }

  const anyUnsure = Object.values(a).some((v) => v === "unsure");
  if (anyUnsure || Object.values(a).some((v) => v === "")) {
    return {
      outcome: "unsure",
      headline: "Can't tell yet — answer the questions, or check with HSE",
      deadline:
        "If in doubt, report without delay and within 10 days — it's safer to check.",
      detail:
        "Some answers are unsure or blank. RIDDOR reportability turns on the detail of the injury. For borderline cases use the official guidance or HSE's incident contact route at hse.gov.uk/riddor.",
    };
  }

  return {
    outcome: "record",
    headline: "This doesn't appear to meet the RIDDOR reportable criteria",
    deadline:
      "Keep an internal record (accident book / incident log). No online RIDDOR report needed on these answers.",
    detail:
      "Based on the answers given this doesn't look reportable, but this isn't a legal ruling — if anything changes (for example the injury worsens or absence passes seven days) re-check, and confirm borderline cases at hse.gov.uk/riddor.",
  };
}

const RIDDOR_TYPES: IncidentType[] = ["injury", "dangerous-occurrence"];

export function IncidentReportBuilder() {
  const [type, setType] = useState<IncidentType>("near-miss");
  const [ref, setRef] = useState("");
  const [date, setDate] = useState("");
  const [time, setTime] = useState("");
  const [location, setLocation] = useState("");
  const [people, setPeople] = useState<PersonRow[]>([emptyPerson()]);
  const [witnesses, setWitnesses] = useState("");
  const [whatHappened, setWhatHappened] = useState("");
  const [injuryTreatment, setInjuryTreatment] = useState("");
  const [immediateActions, setImmediateActions] = useState("");
  const [areaMadeSafe, setAreaMadeSafe] = useState<YN>("");
  const [followUpDate, setFollowUpDate] = useState("");
  const [followUpPerson, setFollowUpPerson] = useState("");

  const [riddor, setRiddor] = useState<RiddorAnswers>(EMPTY_RIDDOR);

  const [immediateCause, setImmediateCause] = useState("");
  const [underlyingCause, setUnderlyingCause] = useState("");
  const [rootCause, setRootCause] = useState("");
  const [actions, setActions] = useState<ActionRow[]>([emptyAction()]);
  const [ramsReview, setRamsReview] = useState<YN>("");

  const [reportedBy, setReportedBy] = useState("");
  const [supervisor, setSupervisor] = useState("");

  const [photos, setPhotos] = useState<Photo[]>([]);

  const [evidence, setEvidence, clearEvidence] = useSavedEvidence();

  const tracked = useRef(false);

  const showRiddor = RIDDOR_TYPES.includes(type);

  const updatePerson = (id: number, patch: Partial<PersonRow>) =>
    setPeople((rs) => rs.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  const updateAction = (id: number, patch: Partial<ActionRow>) =>
    setActions((rs) => rs.map((r) => (r.id === id ? { ...r, ...patch } : r)));

  const riddorVerdict = useMemo(
    () => (showRiddor ? assessRiddor(type, riddor) : null),
    [showRiddor, type, riddor],
  );

  // Top-of-page result banner. A near miss with no injury is the "free lesson"
  // low banner; a likely-RIDDOR injury / occurrence is veryhigh with deadline.
  const banner = useMemo((): { level: Verdict; title: string; body: string } => {
    if (riddorVerdict?.outcome === "likely") {
      return {
        level: "veryhigh",
        title: riddorVerdict.headline,
        body: riddorVerdict.deadline,
      };
    }
    if (riddorVerdict?.outcome === "unsure") {
      return {
        level: "high",
        title: "Possible RIDDOR report — check before you close this out",
        body: riddorVerdict.deadline,
      };
    }
    if (type === "near-miss") {
      return {
        level: "low",
        title: "Recorded — near misses are free lessons",
        body: "No one was hurt this time. Capturing the cause and the fix now is the cheapest way to stop the version that does hurt someone.",
      };
    }
    if (type === "dangerous-occurrence") {
      return {
        level: "high",
        title: "Dangerous occurrence — check RIDDOR reportability",
        body: "Listed dangerous occurrences are reportable even with no injury. Work through the RIDDOR check below.",
      };
    }
    if (type === "minor-injury") {
      return {
        level: "medium",
        title: "Minor injury — record it and act on the cause",
        body: "First-aid injuries aren't usually RIDDOR reportable, but the accident book entry and the corrective action still matter.",
      };
    }
    if (type === "property-damage") {
      return {
        level: "medium",
        title: "Property / plant damage — capture the cause",
        body: "Damage-only events rarely report under RIDDOR, but they often share a root cause with the injury that nearly happened.",
      };
    }
    // injury, RIDDOR answers say record-only / blank
    return {
      level: "high",
      title: "Injury — record it and run the RIDDOR check",
      body: riddorVerdict?.deadline ?? "Work through the RIDDOR questions below to decide if it's reportable.",
    };
  }, [riddorVerdict, type]);

  const hasCore = type !== undefined && (date !== "" || location !== "" || whatHappened !== "");

  // Core fields an incident record shouldn't ship without. RIDDOR answers are
  // only required when the type triggers the reportability check.
  const requiredItems: { label: string; done: boolean }[] = [
    { label: "Date of incident", done: date.trim().length > 0 },
    { label: "Location / area", done: location.trim().length > 0 },
    { label: "What happened", done: whatHappened.trim().length > 0 },
    { label: "Person(s) involved", done: people.some((p) => p.name.trim()) },
    { label: "Immediate / underlying / root cause", done: !!(immediateCause.trim() && underlyingCause.trim() && rootCause.trim()) },
    { label: "At least one corrective action with owner", done: actions.some((a) => a.action.trim() && a.owner.trim()) },
    ...(showRiddor ? [{ label: "RIDDOR check answered", done: Object.values(riddor).every((v) => v !== "") }] : []),
    { label: "Reported by", done: reportedBy.trim().length > 0 },
    { label: "Supervisor / manager", done: supervisor.trim().length > 0 },
    ...evidenceRequiredItems(evidence),
  ];
  const missingRequired = requiredItems.filter((i) => !i.done).map((i) => i.label);

  useEffect(() => {
    if (hasCore && !tracked.current) {
      tracked.current = true;
      trackToolResult("incident-report", {
        type,
        riddor: riddorVerdict?.outcome ?? "n/a",
      });
    }
  }, [hasCore, type, riddorVerdict]);

  const displayDate = (() => {
    if (!date) return "";
    const d = new Date(date + "T00:00:00");
    if (isNaN(d.getTime())) return date;
    return d.toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });
  })();

  return (
    <div className="grid gap-6 lg:grid-cols-[1fr_400px]">
      {/* ── Inputs ─────────────────────────────────────────────────────── */}
      <div className="space-y-6">
        {/* Type + facts */}
        <div className="card shadow-soft p-6">
          <h2 className="text-xl">What happened</h2>
          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            <Field label="Incident type" className="sm:col-span-2">
              <SelectInput value={type} onChange={(e) => setType(e.target.value as IncidentType)}>
                {TYPE_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </SelectInput>
            </Field>
            <Field label="Reference (optional)">
              <TextInput value={ref} onChange={(e) => setRef(e.target.value)} placeholder="e.g. INC-2026-014" />
            </Field>
            <Field label="Location / area">
              <TextInput value={location} onChange={(e) => setLocation(e.target.value)} placeholder="e.g. Plot 4, first-floor scaffold" />
            </Field>
            <Field label="Date">
              <TextInput type="date" value={date} onChange={(e) => setDate(e.target.value)} />
            </Field>
            <Field label="Time">
              <TextInput type="time" value={time} onChange={(e) => setTime(e.target.value)} />
            </Field>
          </div>

          <div className="mt-5">
            <Field
              label="What happened"
              hint="Facts only — what, where, when. No blame, no opinions on fault."
            >
              <TextArea
                rows={5}
                value={whatHappened}
                onChange={(e) => setWhatHappened(e.target.value)}
                placeholder="A factual account of the sequence of events."
              />
            </Field>
          </div>
        </div>

        {/* People */}
        <div className="card shadow-soft p-6">
          <h2 className="text-xl">People involved</h2>
          <div className="mt-4 space-y-4">
            {people.map((p, i) => (
              <div key={p.id} className="rounded-xl border border-line bg-mist/60 p-4">
                <div className="flex items-center justify-between">
                  <span className="text-[0.8rem] font-bold uppercase tracking-wide text-muted">Person {i + 1}</span>
                  {people.length > 1 && (
                    <button
                      type="button"
                      onClick={() => setPeople((rs) => rs.filter((r) => r.id !== p.id))}
                      className="text-[0.8rem] font-semibold text-muted hover:text-risk-veryhigh"
                    >
                      Remove
                    </button>
                  )}
                </div>
                <div className="mt-3 grid gap-3 sm:grid-cols-3">
                  <Field label="Name">
                    <RememberedTextInput store="people" value={p.name} onChange={(e) => updatePerson(p.id, { name: e.target.value })} placeholder="Full name" />
                  </Field>
                  <Field label="Role">
                    <TextInput value={p.role} onChange={(e) => updatePerson(p.id, { role: e.target.value })} placeholder="e.g. Groundworker" />
                  </Field>
                  <Field label="Company">
                    <TextInput value={p.company} onChange={(e) => updatePerson(p.id, { company: e.target.value })} placeholder="Employer" />
                  </Field>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-4">
            <button
              type="button"
              onClick={() => setPeople((rs) => [...rs, emptyPerson()])}
              className="inline-flex items-center gap-1.5 rounded-full border border-line-2 px-4 py-2 text-sm font-semibold text-ink hover:border-ink hover:bg-mist"
            >
              + Add person
            </button>
          </div>
          <div className="mt-4">
            <Field label="Witnesses" hint="Names and how to contact them, if any.">
              <TextArea rows={2} value={witnesses} onChange={(e) => setWitnesses(e.target.value)} placeholder="Witness names / contact details" />
            </Field>
          </div>
          <div className="mt-4">
            <RememberedNamesNote stores={["people"]} />
          </div>
        </div>

        {/* Injury, treatment, immediate actions */}
        <div className="card shadow-soft p-6">
          <h2 className="text-xl">Injury &amp; immediate response</h2>
          <div className="mt-4 space-y-4">
            <Field label="Injury &amp; treatment given" hint="Nature of any injury and the first aid / medical treatment provided.">
              <TextArea
                rows={3}
                value={injuryTreatment}
                onChange={(e) => setInjuryTreatment(e.target.value)}
                placeholder="e.g. Laceration to left forearm, cleaned and dressed by site first aider."
              />
            </Field>
            <Field label="Immediate actions taken" hint="What was done straight away to control the situation?">
              <TextArea
                rows={3}
                value={immediateActions}
                onChange={(e) => setImmediateActions(e.target.value)}
                placeholder="e.g. Tool isolated, area cordoned, supervisor informed."
              />
            </Field>
            <Field label="Was the area made safe?">
              <SelectInput value={areaMadeSafe} onChange={(e) => setAreaMadeSafe(e.target.value as YN)}>
                <option value="">Select…</option>
                <option value="yes">Yes — area made safe</option>
                <option value="no">No — hazard still present</option>
                <option value="unsure">Not sure</option>
              </SelectInput>
            </Field>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Welfare follow-up date" hint="When the injured person will be checked on / return-to-work reviewed.">
                <TextInput type="date" value={followUpDate} onChange={(e) => setFollowUpDate(e.target.value)} />
              </Field>
              <Field label="Follow-up responsible person" hint="Who is responsible for the follow-up with the injured person.">
                <TextInput value={followUpPerson} onChange={(e) => setFollowUpPerson(e.target.value)} placeholder="Name" />
              </Field>
            </div>
          </div>
        </div>

        {/* RIDDOR prompt */}
        {showRiddor && (
          <div className="card shadow-soft border-risk-high/30 p-6">
            <div className="text-[0.74rem] font-semibold uppercase tracking-wide text-[#b34e10]">
              RIDDOR reportability check
            </div>
            <h2 className="mt-1 text-xl">Is this reportable to HSE?</h2>
            <p className="mt-1 text-sm text-muted">
              The <strong>responsible person</strong> (the employer or whoever is in control of the work) reports —
              not the injured person. Answer each question; this is a decision aid, not a legal ruling.
            </p>

            <div className="mt-5 space-y-4">
              <RiddorQuestion
                label="Did anyone die as a result of the incident?"
                value={riddor.death}
                onChange={(v) => setRiddor((r) => ({ ...r, death: v }))}
              />
              <RiddorQuestion
                label="Did a worker suffer a specified injury?"
                value={riddor.specified}
                onChange={(v) => setRiddor((r) => ({ ...r, specified: v }))}
              >
                <DetailList intro="Specified injuries include:" items={SPECIFIED_INJURIES} />
              </RiddorQuestion>
              <RiddorQuestion
                label="Was a worker unable to do their normal duties for more than 7 consecutive days (not counting the day of the accident)?"
                value={riddor.overSeven}
                onChange={(v) => setRiddor((r) => ({ ...r, overSeven: v }))}
              />
              <RiddorQuestion
                label="Was a non-worker (member of the public) taken directly from the scene to hospital for treatment?"
                value={riddor.nonWorkerHospital}
                onChange={(v) => setRiddor((r) => ({ ...r, nonWorkerHospital: v }))}
              />
              <RiddorQuestion
                label="Was this a listed dangerous occurrence?"
                value={riddor.dangerousOccurrence}
                onChange={(v) => setRiddor((r) => ({ ...r, dangerousOccurrence: v }))}
              >
                <DetailList intro="Listed dangerous occurrences include:" items={DANGEROUS_OCCURRENCE_EXAMPLES} />
              </RiddorQuestion>
            </div>

            {riddorVerdict && (
              <div className="mt-5">
                <ResultBanner
                  level={
                    riddorVerdict.outcome === "likely"
                      ? "veryhigh"
                      : riddorVerdict.outcome === "unsure"
                        ? "high"
                        : "low"
                  }
                  title={riddorVerdict.headline}
                >
                  <span className="block font-semibold">{riddorVerdict.deadline}</span>
                  <span className="mt-1 block">{riddorVerdict.detail}</span>
                </ResultBanner>
                <p className="mt-2 text-[0.8rem] text-muted">
                  Borderline? Don&apos;t treat this as a definitive answer — report or check at{" "}
                  <a href="https://www.hse.gov.uk/riddor" rel="noopener" target="_blank" className="font-semibold underline">
                    hse.gov.uk/riddor
                  </a>.
                </p>
              </div>
            )}
          </div>
        )}

        {/* Root cause */}
        <div className="card shadow-soft p-6">
          <h2 className="text-xl">Root cause</h2>
          <p className="mt-1 text-sm text-muted">
            Five-whys, kept short. Work past the obvious to the management arrangement that let it happen.
          </p>
          <div className="mt-4 space-y-4">
            <Field
              label="Immediate cause"
              hint="The unsafe act or condition. e.g. operative stepped onto an unboarded section of scaffold."
            >
              <TextArea rows={2} value={immediateCause} onChange={(e) => setImmediateCause(e.target.value)} />
            </Field>
            <Field
              label="Underlying cause — why was that possible?"
              hint="e.g. scaffold handover hadn't been checked; no board count on the inspection."
            >
              <TextArea rows={2} value={underlyingCause} onChange={(e) => setUnderlyingCause(e.target.value)} />
            </Field>
            <Field
              label="Root cause — what management arrangement failed?"
              hint="e.g. no system to verify scaffold inspections before access is allowed."
            >
              <TextArea rows={2} value={rootCause} onChange={(e) => setRootCause(e.target.value)} />
            </Field>
          </div>

          <h3 className="mt-6 text-lg">Corrective actions</h3>
          <div className="mt-3 space-y-3">
            {actions.map((a, i) => (
              <div key={a.id} className="rounded-xl border border-line bg-mist/60 p-4">
                <div className="flex items-center justify-between">
                  <span className="text-[0.8rem] font-bold uppercase tracking-wide text-muted">Action {i + 1}</span>
                  {actions.length > 1 && (
                    <button
                      type="button"
                      onClick={() => setActions((rs) => rs.filter((r) => r.id !== a.id))}
                      className="text-[0.8rem] font-semibold text-muted hover:text-risk-veryhigh"
                    >
                      Remove
                    </button>
                  )}
                </div>
                <div className="mt-3 grid gap-3 sm:grid-cols-[1fr_auto]">
                  <Field label="Action">
                    <TextInput value={a.action} onChange={(e) => updateAction(a.id, { action: e.target.value })} placeholder="What will be done" />
                  </Field>
                  <div className="grid grid-cols-2 gap-3">
                    <Field label="Owner">
                      <TextInput value={a.owner} onChange={(e) => updateAction(a.id, { owner: e.target.value })} placeholder="Who" />
                    </Field>
                    <Field label="Due date">
                      <TextInput type="date" value={a.due} onChange={(e) => updateAction(a.id, { due: e.target.value })} />
                    </Field>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-4">
            <button
              type="button"
              onClick={() => setActions((rs) => [...rs, emptyAction()])}
              className="inline-flex items-center gap-1.5 rounded-full border border-line-2 px-4 py-2 text-sm font-semibold text-ink hover:border-ink hover:bg-mist"
            >
              + Add action
            </button>
          </div>

          <div className="mt-5">
            <Field label="Does the RAMS for this task need revising?" hint="If the method or controls in the RAMS didn't match what actually happened, update it.">
              <SelectInput value={ramsReview} onChange={(e) => setRamsReview(e.target.value as YN)}>
                <option value="">Select…</option>
                <option value="yes">Yes — RAMS to be revised</option>
                <option value="no">No — RAMS still adequate</option>
                <option value="unsure">To be reviewed</option>
              </SelectInput>
            </Field>
          </div>
        </div>

        {/* Sign-off */}
        <div className="card shadow-soft p-6">
          <h2 className="text-xl">Sign-off</h2>
          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            <Field label="Reported by">
              <TextInput value={reportedBy} onChange={(e) => setReportedBy(e.target.value)} placeholder="Name" />
            </Field>
            <Field label="Supervisor / manager">
              <TextInput value={supervisor} onChange={(e) => setSupervisor(e.target.value)} placeholder="Name" />
            </Field>
          </div>
        </div>

        <PhotoEvidence photos={photos} onChange={setPhotos} />

        <EvidenceFields value={evidence} onChange={setEvidence} onClear={clearEvidence} />
      </div>

      {/* ── Output ─────────────────────────────────────────────────────── */}
      <div className="space-y-4">
        <div className="lg:sticky lg:top-20 lg:z-10">
          <CompletionPanel items={requiredItems} title="Report completion" />
        </div>
        <div className="print-area card shadow-soft p-6" id="incident-record">
          <div className="text-[0.74rem] font-semibold uppercase tracking-wide text-emerald-deep">
            Incident &amp; near-miss report
          </div>
          <h2 className="mt-1 text-lg">
            {TYPE_LABEL[type]}
            {ref ? ` — ${ref}` : ""}
          </h2>
          <p className="mt-0.5 text-sm text-muted">
            {displayDate || "Date not set"}
            {time ? `, ${time}` : ""}
            {location ? ` — ${location}` : ""}
          </p>

          <div className="mt-4">
            <ResultBanner level={banner.level} title={banner.title}>
              {banner.body}
            </ResultBanner>
          </div>

          <ReportSection title="What happened" body={whatHappened} />

          {people.some((p) => p.name || p.role || p.company) && (
            <div className="mt-4">
              <SectionHeading>People involved</SectionHeading>
              <table className="mt-1.5 w-full text-sm">
                <thead>
                  <tr className="border-b border-line text-left text-[0.78rem] uppercase tracking-wide text-muted">
                    <th className="py-1.5 pr-2 font-semibold">Name</th>
                    <th className="py-1.5 pr-2 font-semibold">Role</th>
                    <th className="py-1.5 font-semibold">Company</th>
                  </tr>
                </thead>
                <tbody>
                  {people
                    .filter((p) => p.name || p.role || p.company)
                    .map((p) => (
                      <tr key={p.id} className="border-b border-line/60">
                        <td className="py-2 pr-2 text-ink">{p.name || "—"}</td>
                        <td className="py-2 pr-2">{p.role || "—"}</td>
                        <td className="py-2">{p.company || "—"}</td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          )}

          <ReportSection title="Witnesses" body={witnesses} />
          <ReportSection title="Injury & treatment given" body={injuryTreatment} />
          <ReportSection
            title="Immediate actions taken"
            body={
              immediateActions || areaMadeSafe
                ? `${immediateActions}${immediateActions && areaMadeSafe ? "\n" : ""}${
                    areaMadeSafe === "yes"
                      ? "Area made safe: yes."
                      : areaMadeSafe === "no"
                        ? "Area made safe: no — hazard still present."
                        : areaMadeSafe === "unsure"
                          ? "Area made safe: not confirmed."
                          : ""
                  }`
                : ""
            }
          />

          {(followUpDate || followUpPerson) && (
            <ReportSection
              title="Welfare follow-up"
              body={[
                followUpDate && `Follow-up date: ${followUpDate}.`,
                followUpPerson && `Responsible: ${followUpPerson}.`,
              ]
                .filter(Boolean)
                .join(" ")}
            />
          )}

          {showRiddor && riddorVerdict && (
            <div className="mt-4">
              <SectionHeading>RIDDOR assessment</SectionHeading>
              <p className="mt-1.5 text-sm font-semibold text-ink">{riddorVerdict.headline}</p>
              <p className="mt-0.5 text-sm text-body">{riddorVerdict.deadline}</p>
              <p className="mt-0.5 text-[0.82rem] text-muted">
                Reported by the responsible person (employer / person in control of the work). Guidance only —
                confirm borderline cases at hse.gov.uk/riddor.
              </p>
            </div>
          )}

          {(immediateCause || underlyingCause || rootCause) && (
            <div className="mt-4">
              <SectionHeading>Root cause</SectionHeading>
              <dl className="mt-1.5 space-y-1.5 text-sm">
                {immediateCause && <CauseLine term="Immediate" desc={immediateCause} />}
                {underlyingCause && <CauseLine term="Underlying" desc={underlyingCause} />}
                {rootCause && <CauseLine term="Root" desc={rootCause} />}
              </dl>
            </div>
          )}

          {actions.some((a) => a.action || a.owner || a.due) && (
            <div className="mt-4">
              <SectionHeading>Corrective actions</SectionHeading>
              <table className="mt-1.5 w-full text-sm">
                <thead>
                  <tr className="border-b border-line text-left text-[0.78rem] uppercase tracking-wide text-muted">
                    <th className="py-1.5 pr-2 font-semibold">Action</th>
                    <th className="py-1.5 pr-2 font-semibold">Owner</th>
                    <th className="py-1.5 font-semibold">Due</th>
                  </tr>
                </thead>
                <tbody>
                  {actions
                    .filter((a) => a.action || a.owner || a.due)
                    .map((a) => (
                      <tr key={a.id} className="border-b border-line/60">
                        <td className="py-2 pr-2 text-ink">{a.action || "—"}</td>
                        <td className="py-2 pr-2">{a.owner || "—"}</td>
                        <td className="py-2">{a.due || "—"}</td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          )}

          {ramsReview && (
            <ReportSection
              title="RAMS review"
              body={
                ramsReview === "yes"
                  ? "The RAMS for this task is to be revised."
                  : ramsReview === "no"
                    ? "The RAMS for this task was reviewed and remains adequate."
                    : "The RAMS for this task is to be reviewed."
              }
            />
          )}

          <div className="mt-5 grid grid-cols-2 gap-4 border-t border-line pt-4 text-sm">
            <div>
              <div className="text-[0.74rem] uppercase tracking-wide text-muted">Reported by</div>
              <div className="mt-1 font-semibold text-ink">{reportedBy || "________________"}</div>
            </div>
            <div>
              <div className="text-[0.74rem] uppercase tracking-wide text-muted">Supervisor / manager</div>
              <div className="mt-1 font-semibold text-ink">{supervisor || "________________"}</div>
            </div>
            <div className="col-span-2">
              <div className="text-[0.74rem] uppercase tracking-wide text-muted">Date</div>
              <div className="mt-1 font-semibold text-ink">
                {new Date().toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}
              </div>
            </div>
          </div>

          <PhotoEvidencePrint photos={photos} />

          <EvidencePrint value={evidence} briefing={false} />

          <PrintFooter toolName="incident & near-miss report builder" />
        </div>

        <GuardedPrintButton
          tool="incident-report"
          label="Print / save report as PDF"
          missing={missingRequired}
          warningTitle="This incident report still has empty core fields"
        />
      </div>
    </div>
  );
}

/* ── Small presentational helpers ─────────────────────────────────────── */

function RiddorQuestion({
  label,
  value,
  onChange,
  children,
}: {
  label: string;
  value: YN;
  onChange: (v: YN) => void;
  children?: React.ReactNode;
}) {
  return (
    <div className="rounded-xl border border-line bg-white p-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <span className="text-[0.92rem] font-semibold leading-snug text-ink">{label}</span>
        <div className="flex shrink-0 gap-2">
          {(["yes", "no", "unsure"] as const).map((opt) => (
            <button
              key={opt}
              type="button"
              onClick={() => onChange(opt)}
              className={`rounded-full px-3.5 py-1.5 text-[0.82rem] font-semibold capitalize transition-colors ${
                value === opt
                  ? "bg-ink text-white"
                  : "border border-line-2 text-muted hover:border-ink hover:text-ink"
              }`}
            >
              {opt}
            </button>
          ))}
        </div>
      </div>
      {children && <div className="mt-3">{children}</div>}
    </div>
  );
}

function DetailList({ intro, items }: { intro: string; items: string[] }) {
  return (
    <details className="rounded-lg bg-mist/70 px-3.5 py-2.5 text-[0.82rem] text-body">
      <summary className="cursor-pointer font-semibold text-ink">{intro}</summary>
      <ul className="mt-2 list-disc space-y-1 pl-5 text-muted">
        {items.map((it) => (
          <li key={it}>{it}</li>
        ))}
      </ul>
    </details>
  );
}

function SectionHeading({ children }: { children: React.ReactNode }) {
  return (
    <div className="text-[0.74rem] font-semibold uppercase tracking-wide text-emerald-deep">{children}</div>
  );
}

function ReportSection({ title, body }: { title: string; body: string }) {
  if (!body || !body.trim()) return null;
  return (
    <div className="mt-4">
      <SectionHeading>{title}</SectionHeading>
      <p className="mt-1 whitespace-pre-line text-sm leading-relaxed text-body">{body}</p>
    </div>
  );
}

function CauseLine({ term, desc }: { term: string; desc: string }) {
  return (
    <div className="flex gap-2">
      <dt className="w-24 shrink-0 font-semibold text-ink">{term}</dt>
      <dd className="text-body">{desc}</dd>
    </div>
  );
}

/* ── Photo evidence (client-side only — images never leave the browser) ──── */

interface Photo {
  id: number;
  dataUrl: string;
  caption: string;
}

const MAX_PHOTOS = 6;
let nextPhotoId = 1;

/** Downscales an image file to a JPEG data URL (longest edge ≤ 1200px). */
function downscaleToDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("read failed"));
    reader.onload = () => {
      const img = new Image();
      img.onerror = () => reject(new Error("decode failed"));
      img.onload = () => {
        const maxEdge = 1200;
        const longest = Math.max(img.width, img.height);
        const scale = longest > maxEdge ? maxEdge / longest : 1;
        const w = Math.max(1, Math.round(img.width * scale));
        const h = Math.max(1, Math.round(img.height * scale));
        const canvas = document.createElement("canvas");
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext("2d");
        if (!ctx) {
          reject(new Error("no canvas context"));
          return;
        }
        ctx.drawImage(img, 0, 0, w, h);
        resolve(canvas.toDataURL("image/jpeg", 0.8));
      };
      img.src = reader.result as string;
    };
    reader.readAsDataURL(file);
  });
}

function PhotoEvidence({ photos, onChange }: { photos: Photo[]; onChange: (p: Photo[]) => void }) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [atCap, setAtCap] = useState(false);

  async function handleFiles(files: FileList | null) {
    if (!files || files.length === 0) return;
    const room = MAX_PHOTOS - photos.length;
    if (room <= 0) {
      setAtCap(true);
      return;
    }
    const images = Array.from(files).filter((f) => f.type.startsWith("image/"));
    const taken = images.slice(0, room);
    setAtCap(images.length > room);
    const added: Photo[] = [];
    for (const file of taken) {
      try {
        const dataUrl = await downscaleToDataUrl(file);
        added.push({ id: nextPhotoId++, dataUrl, caption: "" });
      } catch {
        /* skip files that fail to decode */
      }
    }
    if (added.length > 0) onChange([...photos, ...added]);
    if (inputRef.current) inputRef.current.value = "";
  }

  const update = (id: number, caption: string) =>
    onChange(photos.map((p) => (p.id === id ? { ...p, caption } : p)));
  const remove = (id: number) => {
    onChange(photos.filter((p) => p.id !== id));
    setAtCap(false);
  };

  return (
    <div className="card shadow-soft p-6">
      <h2 className="text-xl">Photo evidence</h2>
      <p className="mt-1 text-sm text-muted">
        Photos of the scene, the damage or the conditions — they print with the report.
      </p>
      <div className="mt-4">
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          multiple
          onChange={(e) => handleFiles(e.target.files)}
          className="hidden"
        />
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={photos.length >= MAX_PHOTOS}
          className="inline-flex items-center gap-1.5 rounded-full border border-line-2 px-4 py-2 text-sm font-semibold text-ink hover:border-ink hover:bg-mist disabled:cursor-not-allowed disabled:opacity-50"
        >
          + Add photos
        </button>
      </div>
      <p className="mt-2 text-[0.78rem] text-muted">
        Photos stay in this browser — they are never uploaded.
      </p>
      {atCap && (
        <p className="mt-1.5 text-[0.8rem] font-semibold text-[#b34e10]">
          You can attach up to {MAX_PHOTOS} photos — the rest were left out.
        </p>
      )}
      {photos.length > 0 && (
        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          {photos.map((p) => (
            <div key={p.id} className="rounded-xl border border-line bg-mist/60 p-3">
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img src={p.dataUrl} alt="" className="h-36 w-full rounded-lg object-cover" />
              <div className="mt-2">
                <Field label="Caption">
                  <TextInput
                    value={p.caption}
                    onChange={(e) => update(p.id, e.target.value)}
                    placeholder="What this photo shows"
                  />
                </Field>
              </div>
              <button
                type="button"
                onClick={() => remove(p.id)}
                className="mt-2 text-[0.8rem] font-semibold text-muted hover:text-risk-veryhigh"
              >
                Remove
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function PhotoEvidencePrint({ photos }: { photos: Photo[] }) {
  if (photos.length === 0) return null;
  return (
    <div className="mt-4">
      <SectionHeading>Photo evidence</SectionHeading>
      <div className="mt-2 grid grid-cols-2 gap-4">
        {photos.map((p) => (
          <div key={p.id} className="avoid-break">
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img src={p.dataUrl} alt="" className="w-full rounded-lg border border-line" />
            {p.caption.trim() && (
              <p className="mt-1 text-[0.82rem] leading-snug text-body">{p.caption}</p>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
